import React, { useCallback, useMemo, useState } from 'react'
import { ResponsiveScatterPlot } from '@nivo/scatterplot'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpand } from '@fortawesome/free-solid-svg-icons'
import { CloseIcon } from '../../../Common/SvgIcons/WorkflowIcons' // adjust path if needed
import NivoTextParser from './NivoTextParser'
import Prediction from '../../../../components/modal/NavPanes/Prediction'

const SPLIT_FILTERS = ['Train', 'Test', 'Val'];
const DEFAULT_COLOR = '#4f7cff';
const DOMAIN_PADDING_RATIO = 0.08;
const CHART_MARGIN = { top: 24, right: 140, bottom: 70, left: 90 };
const getAxisNumberFormat = (label) =>
  /\(\s*px\s*\)/i.test(String(label || '')) ? '>-.0f' : '>-.2f';

const getCssColor = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;

  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const FALLBACK_COLORS = {
  Train: DEFAULT_COLOR,
  Test: '#f59e0b',
  Val: '#22c55e',
};

const SPLIT_ALIAS_MAP = {
  train: 'Train',
  training: 'Train',
  'train split': 'Train',
  test: 'Test',
  testing: 'Test',
  'test split': 'Test',
  val: 'Val',
  valid: 'Val',
  validation: 'Val',
  'validation split': 'Val',
  'val split': 'Val',
};

const normalizeSplitValue = (value) =>
  String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();

const getSplitLabel = (value) => SPLIT_ALIAS_MAP[normalizeSplitValue(value)];

const getPointSplit = (point, fallbackSplit) =>
  getSplitLabel(
    point?.split ??
      point?.Split ??
      point?.dataset_split ??
      point?.datasetSplit ??
      point?.dataset ??
      point?.Dataset ??
      point?.set ??
      point?.subset ??
      point?.partition ??
      fallbackSplit
  );

const getBackendColor = (source, split) => {
  if (!source || typeof source !== 'object') return undefined;

  const normalizedSplit = String(split || '').trim();
  const lowerCaseSplit = normalizedSplit.toLowerCase();
  const color = [
    normalizedSplit ? source[`${normalizedSplit} Color`] : undefined,
    lowerCaseSplit ? source[`${lowerCaseSplit}_color`] : undefined,
    lowerCaseSplit ? source[`${lowerCaseSplit}Color`] : undefined,
    source.color,
    source.colour,
    source.hexColor,
    source.hex_code,
    source.hexCode,
  ].find((value) => typeof value === 'string' && value.trim());

  return color?.trim();
};

const getSplitColor = (source, split) =>
  getBackendColor(source, split) || FALLBACK_COLORS[split] || DEFAULT_COLOR;

const getPointPlotValue = (node) => {
  const pointData = node?.data || {};
  const explicitValue =
    pointData.plot_value ??
    pointData.plotValue ??
    pointData.indexValue ??
    pointData.category ??
    pointData.class ??
    pointData.label;
  const serieSplit = getSplitLabel(node?.serieId);
  const serieValue = serieSplit ? null : node?.serieId;

  return (
    explicitValue ??
    serieValue ??
    pointData.name ??
    pointData.id ??
    getPointSplit(pointData, serieSplit) ??
    node?.serieId ??
    node?.formattedX ??
    node?.xValue
  );
};

const ScatterChart = ({ data, xlabel, ylabel, height, title, cssManage, evaluation_id, modelName, task_type, filterPlacement = 'top-right', showLegend = false }) => {
  const [selectedFilters, setSelectedFilters] = useState(SPLIT_FILTERS);

  // added for expand functionality
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [predictions, setPredictions] = useState(false);
  const [selectedPointData, setSelectedPointData] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartTextColor = getCssColor('--text-color', '#333333');

  const normalizedData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const chartMeta = useMemo(() => {
    const splitColorMap = { ...FALLBACK_COLORS };
    const seriesColorMap = {};
    let hasSeriesLevelSplit = false;
    let hasPointLevelSplit = false;
    const allPoints = [];
    const availableSplitSet = new Set();

    normalizedData.forEach((series) => {
      const seriesSplit = getSplitLabel(series?.id);

      if (seriesSplit) {
        hasSeriesLevelSplit = true;
        splitColorMap[seriesSplit] = getSplitColor(series, seriesSplit);
        if ((series?.data || []).length > 0) {
          availableSplitSet.add(seriesSplit);
        }
      }

      seriesColorMap[series?.id] =
        getSplitColor(series, seriesSplit) ||
        series?.color ||
        FALLBACK_COLORS[seriesSplit] ||
        DEFAULT_COLOR;

      (series?.data || []).forEach((point) => {
        const pointSplit = getPointSplit(point);

        if (pointSplit) {
          hasPointLevelSplit = true;
          availableSplitSet.add(pointSplit);
          splitColorMap[pointSplit] =
            getBackendColor(point, pointSplit) ||
            splitColorMap[pointSplit] ||
            FALLBACK_COLORS[pointSplit];
        }

        allPoints.push(point);
      });
    });

    const getDomain = (axisKey) => {
      const values = allPoints
        .map((point) => Number(point?.[axisKey]))
        .filter((value) => Number.isFinite(value));

      if (values.length === 0) {
        return { type: 'linear', min: 0, max: 'auto' };
      }

      const min = Math.min(0, ...values);
      const max = Math.max(...values);
      const range = max - min;
      const padding = range > 0 ? range * DOMAIN_PADDING_RATIO : 1;

      return {
        type: 'linear',
        min,
        max: max <= min ? min + padding : max + padding,
      };
    };

    return {
      splitColorMap,
      seriesColorMap,
      availableSplits: SPLIT_FILTERS.filter((split) => availableSplitSet.has(split)),
      supportsSplitFilters:
        (hasSeriesLevelSplit || hasPointLevelSplit) && availableSplitSet.size > 0,
      hasPointLevelSplit,
      xScale: getDomain('x'),
      yScale: getDomain('y'),
    };
  }, [normalizedData]);

  const filteredData = useMemo(() => {
    if (!chartMeta.supportsSplitFilters) {
      return normalizedData;
    }

    return normalizedData.map((series) => {
      const seriesSplit = getSplitLabel(series?.id);

      if (chartMeta.hasPointLevelSplit) {
        return {
          ...series,
          data: (series?.data || []).filter((point) => {
            const pointSplit = getPointSplit(point, seriesSplit);
            return pointSplit ? selectedFilters.includes(pointSplit) : true;
          }),
        };
      }

      return {
        ...series,
        data:
          seriesSplit && !selectedFilters.includes(seriesSplit)
            ? []
            : series?.data || [],
      };
    });
  }, [
    chartMeta.hasPointLevelSplit,
    chartMeta.supportsSplitFilters,
    normalizedData,
    selectedFilters,
  ]);

  const visiblePointCount = filteredData.reduce(
    (count, series) => count + (series?.data?.length || 0),
    0
  );

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setHoveredPoint(null);
    setSelectedFilters(prevFilters =>
      checked ? [...prevFilters, value] : prevFilters.filter(filter => filter !== value)
    );
  };

  const getCoincidentSplitPoints = useCallback((node) => {
    const pointsBySplit = new Map();

    filteredData.forEach((series) => {
      const seriesSplit = getSplitLabel(series?.id);

      (series?.data || []).forEach((point) => {
        if (
          Number(point?.x) !== Number(node?.xValue) ||
          Number(point?.y) !== Number(node?.yValue)
        ) {
          return;
        }

        const split = getPointSplit(point, seriesSplit) || String(series?.id || 'Data');
        const existing = pointsBySplit.get(split);
        const numericCount = Number(point?.count);

        pointsBySplit.set(split, {
          split,
          count:
            existing && Number.isFinite(existing.numericCount) && Number.isFinite(numericCount)
              ? existing.numericCount + numericCount
              : point?.count ?? existing?.count,
          numericCount:
            existing && Number.isFinite(existing.numericCount) && Number.isFinite(numericCount)
              ? existing.numericCount + numericCount
              : numericCount,
        });
      });
    });

    return [...pointsBySplit.values()].sort(
      (left, right) =>
        SPLIT_FILTERS.indexOf(left.split) - SPLIT_FILTERS.indexOf(right.split)
    );
  }, [filteredData]);

  const getNodeColor = (node) => {
    const split = getPointSplit(node?.data, getSplitLabel(node?.serieId));
    return (
      getBackendColor(node?.data, split) ||
      chartMeta.splitColorMap[split] ||
      chartMeta.seriesColorMap[node?.serieId] ||
      DEFAULT_COLOR
    );
  };

  const handlePointClick = useCallback((node) => {
    if (!isModalOpen) return;
    if (!evaluation_id) return;

    const plotValue = getPointPlotValue(node);
    if (plotValue === null || plotValue === undefined || plotValue === '') return;

    setSelectedPointData({
      ...node,
      chartTitle: title,
      indexValue: plotValue,
    });
    setPredictions(true);
  }, [evaluation_id, isModalOpen, title]);

  const renderChart = (chartId) => (
    <>
      <ResponsiveScatterPlot
        data={filteredData}
        margin={CHART_MARGIN}
        xScale={chartMeta.xScale}
        xFormat={getAxisNumberFormat(xlabel)}
        yScale={chartMeta.yScale}
        yFormat={getAxisNumberFormat(ylabel)}
        blendMode="multiply"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          orient: 'bottom',
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: xlabel,
          legendPosition: 'middle',
          legendOffset: 46,
        }}
        axisLeft={{
          orient: 'left',
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: ylabel,
          legendPosition: 'middle',
          legendOffset: -60,
        }}
        colors={({ serieId }) =>
          chartMeta.splitColorMap[getSplitLabel(serieId)] ||
          chartMeta.seriesColorMap[serieId] ||
          DEFAULT_COLOR
        }
        nodeComponent={({ node }) => (
          <circle
            cx={node.x}
            cy={node.y}
            r={node.size / 2}
            fill={getNodeColor(node)}
          />
        )}
        onClick={handlePointClick}
        tooltip={() => null}
        onMouseEnter={(node) => setHoveredPoint({ chartId, node })}
        onMouseMove={(node) => setHoveredPoint({ chartId, node })}
        onMouseLeave={() => setHoveredPoint(null)}
        legends={showLegend && !chartMeta.supportsSplitFilters ? [
          {
            anchor: 'right',
            direction: 'column',
            translateX: 125,
            itemsSpacing: 6,
            itemWidth: 110,
            itemHeight: 18,
            itemTextColor: chartTextColor,
            itemDirection: 'left-to-right',
            symbolSize: 10,
            symbolShape: 'circle',
          },
        ] : []}
        theme={{
          axis: {
            ticks: {
              line: {
                stroke: 'currentColor',
              },
              text: {
                fill: chartTextColor,
                fontSize: 11,
                fontWeight: 700,
              },
            },
            legend: {
              text: {
                fill: chartTextColor,
                fontSize: 11,
                fontWeight: 700,
              },
            },
          },
          tooltip: {
            container: {
              background: 'var(--bg-color)',
            },
          },
        }}
      />

      {hoveredPoint?.chartId === chartId && (() => {
        const { node } = hoveredPoint;
        const coincidentPoints = getCoincidentSplitPoints(node);
        const renderBelow = node.y < 110;

        return (
          <div
            style={{
              position: 'absolute',
              left: CHART_MARGIN.left + node.x,
              top: CHART_MARGIN.top + node.y,
              transform: renderBelow
                ? 'translate(-50%, 10px)'
                : 'translate(-50%, calc(-100% - 10px))',
              zIndex: 3,
              pointerEvents: 'none',
              padding: '7px 10px',
              background: 'var(--bg-color)',
              border: '1px solid var(--glass-color)',
              borderRadius: 4,
              color: 'var(--text-color)',
              fontSize: 12,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px var(--shadow-color)',
            }}
          >
            <div>
              {xlabel}: {node.formattedX}, {ylabel}: {node.formattedY}
            </div>
            {coincidentPoints.map(({ split, count }) => (
              <div key={split}>
                <strong style={{ color: chartMeta.splitColorMap[split] || undefined }}>
                  {split}
                </strong>
                {count !== undefined && count !== null ? ` — Images Count: ${count}` : ''}
              </div>
            ))}
          </div>
        );
      })()}

      {visiblePointCount === 0 && (
        <div
          className="d-flex align-items-center justify-content-center text-secondary"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
          }}
        >
          No points for selected filters
        </div>
      )}
    </>
  );

  const renderFilters = () =>
    chartMeta.supportsSplitFilters && (
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'absolute',
          ...(filterPlacement === 'right-center'
            ? { top: '50%', transform: 'translateY(-50%)' }
            : filterPlacement === 'bottom-right'
              ? { bottom: 8 }
              : { top: -45 }),
          right: 12,
          display: 'flex',
          flexDirection: filterPlacement === 'right-center' ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: '8px 12px',
          zIndex: filterPlacement === 'top-right' ? 0 : 2,
          alignItems: filterPlacement === 'right-center' ? 'flex-start' : 'center',
          justifyContent: filterPlacement === 'right-center' ? 'center' : 'flex-end',
          maxWidth: 'calc(100% - 24px)',
          padding: filterPlacement === 'right-center' ? '6px 0' : '6px 8px',
          border: filterPlacement === 'right-center' ? 'none' : '1px solid var(--glass-color)',
          borderRadius: filterPlacement === 'right-center' ? 0 : '8px',
          background: filterPlacement === 'right-center' ? 'transparent' : 'var(--bg-color3)',
        }}
      >
        {chartMeta.availableSplits.map(filter => (
          <label
            key={filter}
            className="mb-0"
            style={{
              color: chartMeta.splitColorMap[filter],
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <input
              type="checkbox"
              value={filter}
              checked={selectedFilters.includes(filter)}
              onChange={handleCheckboxChange}
              style={{
                margin: 0,
                marginRight: 8,
                accentColor: chartMeta.splitColorMap[filter],
              }}
            />
            {filter}
          </label>
        ))}
      </div>
    );

  return (
    <>
      <div
        className={`chart ${title === "Classwise keypoints distribution" ? "pb-5" : ""} ${!cssManage && 'badge-border p-4'} `} 
        style={{ height: height ?? "400px", position: 'relative' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          if (!isModalOpen) setIsModalOpen(true);
        }}
      >
        {renderFilters()}
        <NivoTextParser title={title} />

        {isHovered && (
          <button
            type="button"
            className="plain-button chart-expand-control"
            aria-label={`Expand ${title || 'scatter chart'}`}
            title="Expand"
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        )}

        {renderChart('inline')}
      </div>

      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 6,
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className='pt-0 pe-0 container-card w-75 w-md-100'
            style={{
              backgroundColor: 'var(--bg-color8)',
              padding: '50px',
              height: '70%',
              position: 'relative',
              overflow: 'visible',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close expanded chart"
              className='plain-button text-danger'
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(false);
              }}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                cursor: 'pointer',
                zIndex: 3,
              }}
            >
              <CloseIcon className='wf-icon text-secondary' />
            </button>

            <div
              className='p-3'
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
              }}
            >
              {title && (
                <div style={{ height: '50px' }}>
                  <NivoTextParser title={title} />
                </div>
              )}
              <div
                style={{
                  height: title ? 'calc(100% - 50px)' : '100%',
                  position: 'relative',
                }}
              >
                {renderFilters()}
                {renderChart('modal')}
              </div>
            </div>
          </div>
        </div>
      )}

      {predictions && (
        <div
          className="sample-bg mt-0 mt-md-4"
          style={{
            position: "fixed",
            top: "1",
          }}
          onClick={() => setPredictions(false)}
        >
          <div
            className="modal-905 w-100 mx-0 mb-0"
            onClick={(event) => event.stopPropagation()}
          >
            <Prediction
              type="bars"
              sliderIndex={sliderIndex}
              setSliderIndex={setSliderIndex}
              setPredictions={setPredictions}
              barData={selectedPointData}
              bar_url="evaluations"
              id={evaluation_id}
              modelName={modelName}
              tasktype={task_type}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default ScatterChart
